// Admin user-management endpoints (Multi-user Stage B.3).
// All routes server-side gated on `ADMIN_EMAILS`. Non-admins get 404.

import { apiDelete, apiGet, apiPatch, apiPost } from "./client";

export interface AdminUser {
  user_id: string;
  monthly_cap_usd: string;
  disabled: boolean;
  welcomed_at: string | null;
  mtd_spend_usd: string;
  mtd_trace_count: number;
  last_active_at: string | null;
  profile_created_at: string | null;
  caps_updated_at: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
}

export interface AdminHealthResponse {
  ok: boolean;
}

export interface AdminUserPatch {
  monthly_cap_usd?: string;
  disabled?: boolean;
}

export function fetchAdminUsers(): Promise<AdminUsersResponse> {
  return apiGet<AdminUsersResponse>("/admin/users");
}

export function fetchAdminHealth(): Promise<AdminHealthResponse> {
  return apiGet<AdminHealthResponse>("/admin/health");
}

export function patchAdminUser(
  userId: string,
  patch: AdminUserPatch,
): Promise<{ updated: boolean; user_id: string }> {
  // Encode the user_id — Auth0 subs contain '|' which axios will URL-encode
  // for us, but doing it explicitly keeps the URL stable across libraries.
  const path = `/admin/users/${encodeURIComponent(userId)}`;
  return apiPatch<{ updated: boolean; user_id: string }, AdminUserPatch>(path, patch);
}

export function deleteAdminUser(
  userId: string,
): Promise<{ deleted: boolean; user_id: string }> {
  const path = `/admin/users/${encodeURIComponent(userId)}`;
  return apiDelete<{ deleted: boolean; user_id: string }>(path);
}

// ----- /admin/invites (Stage B.3b) -----------------------------------------

export interface AllowlistEntry {
  email: string;
  invited_by: string | null;
  invited_at: string | null;
  revoked_at: string | null;
}

export interface InvitesResponse {
  invites: AllowlistEntry[];
}

export interface InviteCreateResponse {
  invite: AllowlistEntry;
  auth0:
    | { exists: false }
    | { exists: true; user_id: string; email_verified: boolean };
  share_url: string;
}

export function fetchInvites(): Promise<InvitesResponse> {
  return apiGet<InvitesResponse>("/admin/invites");
}

export function createInvite(email: string): Promise<InviteCreateResponse> {
  return apiPost<InviteCreateResponse, { email: string }>("/admin/invites", { email });
}

export function revokeInvite(email: string): Promise<{ revoked: boolean; email: string }> {
  return apiDelete<{ revoked: boolean; email: string }>(
    `/admin/invites/${encodeURIComponent(email)}`,
  );
}
